import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Button,
  Modal,
  Fade,
  Backdrop,
  Table,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  CircularProgress
} from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPlan,
  getUserPackage,
  getActivePlans
} from "../../actions";
import moment from "moment";
import "./subscriptions-list.scss";
import { REPLAY_MASK } from "../../utils/replay-mask";

export default function CurrentPlanView(props) {
  const { paymentHistory, subcriptionEndConfirm, activePlans, showPlanDetails, isMobileBilling } = props;
  const [endPlanId, setEndPlanId] = useState(null);
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();


  const selectPlanRes = useSelector((state) => {
    return state.selectPlan;
  });

  const onSubcriptionEnd = (type, id = null) => {
    setOpen(type);

    if (id) {
      setEndPlanId(id);
    }
  };

  const onSubcriptionEndConfirm = () => {
    subcriptionEndConfirm(endPlanId);
    onSubcriptionEnd(false);
  };

  useEffect(() => {
    if (selectPlanRes?.loading === false) {
      const userId = window.localStorage.getItem("userId");
      dispatch(getUserPackage(userId));
      dispatch(getActivePlans(userId));
    }
  }, [dispatch, selectPlanRes]);

  const onSelectPlan = (row) => {
    const userId = window.localStorage.getItem("userId");

    dispatch(selectPlan({
      userId,
      planId: row?.plan?.id
    }));
  }

  const renderActivePlanActions = (row) => (
    <Box className="active-actions">
      {!row.iselected && !row.expired &&
        <Button variant="contained" color="primary" onClick={() => onSelectPlan(row)}>
          Select
        </Button>
      }
      {row.iselected && !row.expired &&
        <Box className="active-cell">
          ACTIVE
          {row.status === 'CANCELLED' &&
            <Box component="span" className="cancel-date">(until {moment(row.subscriptionEndDt).format("MMM D")})</Box>
          }
        </Box>
      }
      {!row.expired && row.status !== 'CANCELLED'
        && row?.plan?.category?.toLowerCase() !== 'free' &&
        <Button variant="contained" className="end-sub-btn" onClick={() => onSubcriptionEnd(true, row?.subscriptionId)}>
          Cancel
        </Button>
      }
    </Box>
  );

  return (
    // Masked as one box (M3): the student's active plans with trial and
    // renewal dates, and the billing history with prices paid and dates. Every
    // row added later inherits it.
    <Box className={`sub-wrapper current-plan ${isMobileBilling ? 'current-plan-mobile' : ''}`} {...REPLAY_MASK}>
      <Backdrop open={activePlans?.loading || false} className="quiz-loader">
        <CircularProgress color="inherit" />
      </Backdrop>

      {isMobileBilling &&
        <>
          <Box className="mobile-plan-section">
            {/* The list includes expired rows — "Active Plans" over an
                "expired Jul 9" row reads contradictory. Web table unchanged. */}
            <Typography variant="h6" className="title">
              Your Plans
            </Typography>
            <Box className="mobile-plan-content">
              {activePlans?.plansList?.length > 0 &&
                activePlans?.plansList?.map((row, index) => (
                  <Box key={index} className={`mobile-plan-row ${row.iselected ? 'selected' : ''} ${row.expired ? 'expired-plan-row' : ''}`}>
                    <Box>
                      <Typography className="mobile-plan-name">{row?.plan?.name}</Typography>
                      {/* No backend price on this native surface: next to
                          "renews <date>" it reads as the renewal amount, which
                          for an App Store subscription is the StoreKit price
                          (and localized) — the backend figure can misstate it
                          (S1 class rule: StoreKit-localized or withheld). */}
                      <Typography variant="caption" color="textSecondary">
                        {!row.expired && row.subscriptionEndDt && row?.plan?.category?.toLowerCase() !== 'free' &&
                          <>Renews {moment(row.subscriptionEndDt).format("MMM D, YYYY")}</>
                        }
                        {row.expired &&
                          <>Expired {moment(row.subscriptionEndDt).format("MMM D, YYYY")}</>
                        }
                      </Typography>
                      {row.iselected && !row.expired &&
                        <Typography variant="caption" className="show-plan-details"
                          onClick={() => showPlanDetails(row)}>Show Details</Typography>
                      }
                    </Box>
                    {renderActivePlanActions(row)}
                  </Box>
                ))
              }
              {(!activePlans?.plansList || activePlans?.plansList?.length === 0) &&
                <Typography className="mobile-empty-state">No active subscriptions found.</Typography>
              }
            </Box>
          </Box>

          <Box className="mobile-plan-section">
            <Typography variant="h6" className="title">
              Billing History
            </Typography>
            <Box className="mobile-plan-content">
              {paymentHistory && paymentHistory?.length > 0 &&
                paymentHistory.map((row, index) => (
                  <Box key={index} className="mobile-plan-row">
                    <Box>
                      <Typography className="mobile-plan-name">{row?.plan?.name}</Typography>
                      {/* Billing-history amounts are withheld natively: an App
                          Store charge is the (localized) StoreKit price, so the
                          backend dollar figure can misstate what was actually
                          paid (S1 class rule). App Store purchase history
                          carries the authoritative amounts. */}
                      <Typography variant="caption" color="textSecondary">
                        {row?.plan?.category} - {row.status}
                      </Typography>
                    </Box>
                    {row?.subscriptionStartDate &&
                      <Typography variant="caption">{moment(row?.subscriptionStartDate).format("MMM D, YYYY")}</Typography>
                    }
                  </Box>
                ))
              }
              {(!paymentHistory || paymentHistory?.length === 0) &&
                <Typography className="mobile-empty-state">No billing history found.</Typography>
              }
            </Box>
          </Box>
        </>
      }

      {!isMobileBilling &&
        <>
      <Box className="table-cont-wrapper">
        <Box className="plan-table">
          <Typography variant="h6" className="title">
            Active Plans
            <Typography variant="caption" className="sub-title">Select to switch active plan</Typography>
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Renewal Date</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activePlans?.plansList?.length > 0 &&
                <>
                  {activePlans?.plansList?.map((row, index) => (
                    <TableRow key={index} className={`${row.iselected ? 'selected' : ''} ${row.expired ? 'expired-plan-row' : ''}`}>
                      <TableCell>
                        {row?.plan?.name}
                        {row.iselected && !row.expired &&
                          <Typography variant="caption" className="show-plan-details"
                            onClick={() => showPlanDetails(row)}>Show Details</Typography>
                        }
                      </TableCell>
                      <TableCell>
                        ${row?.plan?.price}
                      </TableCell>
                      {/* <TableCell>
                          {row.status}
                        </TableCell> */}
                      <TableCell>
                        {!row.expired &&
                          <>{row.subscriptionEndDt && moment(row.subscriptionStartDt).format("ll")}</>
                        }
                      </TableCell>
                      <TableCell>
                        <>
                          {!row.expired && row?.plan?.category?.toLowerCase() !== 'free' &&
                            <>
                              {row.status === 'TRIALING' && row.subscriptionEndDt &&
                                <Typography variant="caption" component="span" display="block" className="trial-offer-text">
                                  Free trial — converts to paid on {moment(row.subscriptionEndDt).format("ll")}
                                </Typography>
                              }
                              {row.status !== 'TRIALING' &&
                                <>{row.subscriptionEndDt && moment(row.subscriptionEndDt).format("ll")}</>
                              }
                            </>
                          }
                          {row.expired &&
                            <>Expired on ({moment(row.subscriptionEndDt).format("ll")})</>
                          }
                        </>
                      </TableCell>
                      <TableCell className="active-actions">
                        {!row.iselected && !row.expired &&
                          <Button variant="contained" color="primary" onClick={() => onSelectPlan(row)}>
                            Select
                          </Button>
                        }
                        {row.iselected && !row.expired &&
                          <Box className="active-cell">
                            ACTIVE
                            {row.status === 'CANCELLED' &&
                              <Box component="span" className="cancel-date">(until {moment(row.subscriptionEndDt).format("MMM D")})</Box>
                            }
                          </Box>
                        }
                        {!row.expired && row.status !== 'CANCELLED'
                          && row?.plan?.category?.toLowerCase() !== 'free' &&
                          <Button variant="contained" className="end-sub-btn" onClick={() => onSubcriptionEnd(true, row?.subscriptionId)}>
                            Cancel
                          </Button>
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              }
              {(!activePlans?.plansList || activePlans?.plansList?.length === 0) &&
                <TableRow><TableCell colSpan="6" className="row-center">No active subscriptions found.</TableCell></TableRow>
              }
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Web hides an empty Billing History: next to the plan cards, an empty
          "No billing history found" table is noise for a free student deciding
          on a trial (paywall CRO audit 2026-11-04). Native keeps its section. */}
      {paymentHistory?.length > 0 &&
      <Box className="table-cont-wrapper">
        {/* <KeyboardBackspace className="nav-btn" onClick={() => setShowBilling(false)} color="primary" /> */}
        <Box className="plan-table">
          <Typography variant="h6" className="title">
            Billing History
          </Typography>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Plan</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Order Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentHistory && paymentHistory?.length > 0 &&
                <>
                  {paymentHistory.map((row, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {row?.plan?.name}
                      </TableCell>
                      <TableCell>
                        {row?.plan?.category}
                      </TableCell>
                      <TableCell>
                        ${row.price}
                      </TableCell>
                      <TableCell>
                        {row.status}
                      </TableCell>
                      <TableCell>
                        {row?.subscriptionStartDate && moment(row?.subscriptionStartDate).format("ll")}
                      </TableCell>
                    </TableRow>
                  ))}
                </>
              }
            </TableBody>
          </Table>
        </Box>
      </Box>
      }
        </>
      }

      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        className='coupon-modal sub-coupon-modal end-sub-modal'
        open={open}
        onClose={() => onSubcriptionEnd(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={open}>
          <Box className='modal-container'>
            <Typography variant="h5" className="header">End Subscription?</Typography>
            <Typography variant="body2" >
              You will lose access to this subscription after the expiration date.
            </Typography>

            <Box className="apply-btn">
              <Button variant="contained" onClick={() => onSubcriptionEnd(false)}>
                No
              </Button>
              <Button variant="contained" className="end-sub-btn" color="primary" onClick={onSubcriptionEndConfirm}>
                Yes
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box >
  );
}
